import React from 'react';
import { Lightbulb, Sun, Zap, Sparkles, Airplay } from 'lucide-react';

const LampParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  lampTypes 
}) => {
  return (
    <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Lightbulb className="w-5 h-5 mr-2 text-amber-500" />
        Параметры источников света
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип источника света */}
        <div>
          <label htmlFor="lampType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип источника света
          </label>
          <select
            id="lampType"
            name="lampType"
            value={formData.lampType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.lampType ? 'input-error' : ''}`}
          >
            {lampTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.lampType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.lampType}
            </p>
          )}
        </div>

        {/* Тип крепления светильника */}
        <div>
          <label htmlFor="ceilingMountType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип крепления светильника
          </label>
          <select
            id="ceilingMountType"
            name="ceilingMountType"
            value={formData.ceilingMountType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.ceilingMountType ? 'input-error' : ''}`}
          >
            <option value="standard">Стандартное (накладной)</option>
            <option value="suspended">Подвесной</option>
            <option value="recessed">Встраиваемый</option>
            <option value="track">Трековый</option>
            <option value="wall">Настенный</option>
          </select>
          {errors.ceilingMountType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.ceilingMountType}
            </p>
          )}
        </div>

        {/* Световой поток */}
        <div>
          <label htmlFor="lampFlux" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Sun className="w-4 h-4 mr-2 text-amber-500" />
            Световой поток лампы, лм
          </label>
          <input
            type="text"
            id="lampFlux"
            name="lampFlux"
            value={formData.lampFlux}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.lampFlux ? 'input-error' : ''}`}
            placeholder="Введите световой поток"
          />
          {errors.lampFlux && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.lampFlux}
            </p>
          )}
        </div>

        {/* Мощность лампы */}
        <div>
          <label htmlFor="lampPower" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Zap className="w-4 h-4 mr-2 text-amber-500" />
            Мощность лампы, Вт
          </label>
          <input
            type="text"
            id="lampPower"
            name="lampPower"
            value={formData.lampPower}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.lampPower ? 'input-error' : ''}`}
            placeholder="Введите мощность"
          />
          {errors.lampPower && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.lampPower}
            </p>
          )}
        </div>

        {/* Световая отдача (эффективность) */}
        <div>
          <label htmlFor="lampEfficiency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
            Световая отдача, лм/Вт
          </label>
          <input
            type="text"
            id="lampEfficiency"
            name="lampEfficiency"
            value={formData.lampEfficiency}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.lampEfficiency ? 'input-error' : ''}`}
            placeholder="Введите эффективность"
          />
          {errors.lampEfficiency && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.lampEfficiency}
            </p>
          )}
        </div>

        {/* Цветовая температура */}
        <div>
          <label htmlFor="colorTemperature" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Airplay className="w-4 h-4 mr-2 text-amber-500" />
            Цветовая температура, K
          </label>
          <select
            id="colorTemperature"
            name="colorTemperature"
            value={formData.colorTemperature}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="2700">2700K - Теплый свет (домашний)</option>
            <option value="3000">3000K - Теплый белый</option>
            <option value="4000">4000K - Нейтральный белый</option>
            <option value="5000">5000K - Холодный белый</option>
            <option value="6500">6500K - Дневной свет</option>
          </select>
        </div>

        {/* Количество ламп */}
        <div className="col-span-full">
          <div className="bg-amber-100 dark:bg-amber-900/40 p-3 rounded-lg border border-amber-200 dark:border-amber-800 mt-2">
            <p className="text-xs text-amber-800 dark:text-amber-300 italic">
              Наш калькулятор рассчитает рекомендуемое количество светильников. Мы учитываем коэффициенты отражения поверхностей, тип лампы, особенности помещения и нормативы СП 52.13330.2016.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LampParametersSection;